import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface PlaylistGenerationSettings {
  playlistName: string;
  playlistLength: number;
  selectedGenres: string[];
  historyFilterDays: number;
  maximumPlayCount: number;
  popularity?: { min: number; max: number };
  yearRange?: { min: number; max: number };
  excludeRecentTracks: boolean;
  excludeDislikedArtists: boolean;
  // Which AI generates playlists; 'auto' (or unset) uses the server default, falling back to the other
  aiProvider?: 'auto' | 'gemini' | 'openai' | 'codex';
}

export type AIProviderName = 'gemini' | 'openai' | 'codex';

export interface CodexLoginInfo {
  url: string;
  code: string;
  expiresAt: string;
}

export interface AIProviderStatus {
  gemini: { configured: boolean };
  openai: { configured: boolean };
  codex: {
    configured: boolean;
    allowed: boolean;
    signedIn: boolean;
    pending: CodexLoginInfo | null;
    error: string | null;
  };
  defaultProvider: AIProviderName;
}

export interface UserPreferences {
  autoRemoveDuplicates?: boolean;
  voiceControlsEnabled?: boolean;
  defaultPlaybackDevice?: string;
}

export interface ThemePreferences {
  mode: 'light' | 'dark';
}

export interface UIViewPreferences {
  playlistsView?: 'grid' | 'list';
  sortOrder?: string;
}

export interface PromptPreset {
  id: string;
  name: string;
  prompt: string;
}

export interface PlaylistMetadata {
  isDynamic?: boolean;
  useSimilarContext?: boolean;
  prompt?: string;
  moods?: string[];
}

export interface PlaybackPosition {
  playlistId: string;
  trackId: string;
  trackUri?: string;
  positionMs: number;
  durationMs?: number;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BackendStorageService {
  private baseUrl = `${environment.apiUrl}/user-data`;
  private cache: Map<string, BehaviorSubject<any>> = new Map();
  private isInitialized = false;

  constructor(private http: HttpClient) {}

  /**
   * Initialize user data for new users
   */
  initializeNewUser(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/initialize`, {}).pipe(
      tap(() => {
        this.isInitialized = true;
        this.clearCache();
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Get all user data at once (useful for initial load)
   */
  getAllUserData(): Observable<Record<string, any>> {
    return this.http.get<Record<string, any>>(`${this.baseUrl}/all`).pipe(
      tap(data => {
        // Cache all data
        Object.keys(data).forEach(key => {
          this.setCacheValue(key, data[key]);
        });
        this.isInitialized = true;
      }),
      catchError(this.handleError)
    );
  }

  // Playlist Generation Settings
  getPlaylistSettings(): Observable<PlaylistGenerationSettings> {
    return this.getCachedData('playlist_settings', () =>
      this.http.get<PlaylistGenerationSettings>(`${this.baseUrl}/playlist-settings`)
    );
  }

  updatePlaylistSettings(settings: Partial<PlaylistGenerationSettings>): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/playlist-settings`, settings).pipe(
      tap(() => {
        this.updateCacheValue('playlist_settings', settings);
      }),
      catchError(this.handleError)
    );
  }

  // User Preferences
  getUserPreferences(): Observable<UserPreferences> {
    return this.getCachedData('preferences', () =>
      this.http.get<UserPreferences>(`${this.baseUrl}/preferences`)
    );
  }

  updateUserPreferences(preferences: Partial<UserPreferences>): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/preferences`, preferences).pipe(
      tap(() => {
        this.updateCacheValue('preferences', preferences);
      }),
      catchError(this.handleError)
    );
  }

  // Theme Preferences
  getThemePreferences(): Observable<ThemePreferences> {
    return this.getCachedData('theme', () =>
      this.http.get<ThemePreferences>(`${this.baseUrl}/theme`)
    );
  }

  updateThemePreferences(theme: Partial<ThemePreferences>): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/theme`, theme).pipe(
      tap(() => {
        this.updateCacheValue('theme', theme);
      }),
      catchError(this.handleError)
    );
  }

  // UI Preferences
  getUIPreferences(): Observable<UIViewPreferences> {
    return this.getCachedData('ui_preferences', () =>
      this.http.get<UIViewPreferences>(`${this.baseUrl}/ui-preferences`)
    );
  }

  updateUIPreferences(ui: Partial<UIViewPreferences>): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ui-preferences`, ui).pipe(
      tap(() => {
        this.updateCacheValue('ui_preferences', ui);
      }),
      catchError(this.handleError)
    );
  }

  // Playback Position (per-playlist resume position)
  getPlaybackPositions(): Observable<Record<string, PlaybackPosition>> {
    return this.http.get<Record<string, PlaybackPosition>>(`${this.baseUrl}/playback-positions`).pipe(
      catchError(this.handleError)
    );
  }

  savePlaybackPosition(position: PlaybackPosition): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/playback-position`, position).pipe(
      catchError(this.handleError)
    );
  }

  // Play History
  getPlayHistory(options?: { days?: number; minPlayCount?: number; limit?: number; filterPlaylist?: string }): Observable<any[]> {
    // If filtering options are provided, don't use cache - make a fresh request
    if (options && (options.days || options.minPlayCount || options.filterPlaylist)) {
      const params = new URLSearchParams();
      if (options.days) params.set('days', options.days.toString());
      if (options.minPlayCount) params.set('minPlayCount', options.minPlayCount.toString());
      if (options.limit) params.set('limit', options.limit.toString());
      if (options.filterPlaylist) params.set('filterPlaylist', options.filterPlaylist);

      const queryString = params.toString();
      const url = queryString ? `${this.baseUrl}/play-history?${queryString}` : `${this.baseUrl}/play-history`;

      return this.http.get<any[]>(url).pipe(
        catchError(this.handleError)
      );
    }

    // For unfiltered requests, use cached data
    return this.getCachedData('play_history', () =>
      this.http.get<any[]>(`${this.baseUrl}/play-history`)
    );
  }

  addToPlayHistory(trackData: any): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/play-history`, trackData).pipe(
      tap(() => {
        // Update cache by adding to the beginning of the array
        const currentHistory = this.getCacheValue<any[]>('play_history') || [];
        const updatedHistory = [trackData, ...currentHistory].slice(0, 1000);
        this.setCacheValue('play_history', updatedHistory);
      }),
      catchError(this.handleError)
    );
  }

  clearPlayHistory(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/play-history`).pipe(
      tap(() => {
        this.setCacheValue('play_history', []);
      }),
      catchError(this.handleError)
    );
  }

  // Get detailed play history (individual records, no aggregation)
  // Used by listening history page to show each play as its own row
  getPlayHistoryDetailed(options?: { limit?: number; filterPlaylist?: string }): Observable<any[]> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.filterPlaylist) params.set('filterPlaylist', options.filterPlaylist);

    const queryString = params.toString();
    const url = queryString
      ? `${this.baseUrl}/play-history-detailed?${queryString}`
      : `${this.baseUrl}/play-history-detailed`;

    return this.http.get<any[]>(url).pipe(
      catchError(this.handleError)
    );
  }

  // API Keys Management
  getAIProviderStatus(): Observable<AIProviderStatus> {
    return this.http.get<AIProviderStatus>(`${this.baseUrl}/ai-providers`).pipe(
      catchError(this.handleError)
    );
  }

  startCodexLogin(): Observable<CodexLoginInfo> {
    return this.http.post<CodexLoginInfo>(`${this.baseUrl}/codex/login`, {}).pipe(
      catchError(this.handleError)
    );
  }

  codexLogout(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/codex/logout`, {}).pipe(
      catchError(this.handleError)
    );
  }

  getApiKeyInfo(service: string): Observable<{ hasKey: boolean; hint: string | null }> {
    return this.http.get<{ hasKey: boolean; hint: string | null }>(`${this.baseUrl}/api-keys/${service}`).pipe(
      catchError(this.handleError)
    );
  }

  saveApiKey(service: string, apiKey: string): Observable<{ message: string; hint: string | null }> {
    return this.http.post<{ message: string; hint: string | null }>(`${this.baseUrl}/api-keys/${service}`, { apiKey }).pipe(
      catchError(this.handleError)
    );
  }

  deleteApiKey(service: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api-keys/${service}`).pipe(
      catchError(this.handleError)
    );
  }

  testApiKey(service: string, apiKey: string): Observable<{ valid: boolean; message?: string }> {
    return this.http.post<{ valid: boolean; message?: string }>(`${this.baseUrl}/api-keys/test`, { service, apiKey }).pipe(
      catchError(this.handleError)
    );
  }

  // Playlist Preferences (per-playlist settings)
  getPlaylistPreference(playlistId: string): Observable<PlaylistMetadata> {
    return this.http.get<PlaylistMetadata>(
      `${this.baseUrl}/playlist-preferences/${playlistId}`
    ).pipe(catchError(this.handleError));
  }

  updatePlaylistPreference(playlistId: string, prefs: Partial<PlaylistMetadata>): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/playlist-preferences/${playlistId}`,
      prefs
    ).pipe(catchError(this.handleError));
  }

  getAllPlaylistPreferences(): Observable<Record<string, PlaylistMetadata>> {
    return this.http.get<Record<string, PlaylistMetadata>>(
      `${this.baseUrl}/playlist-preferences`
    ).pipe(catchError(this.handleError));
  }

  // Prompt presets (named, reusable generation prompts)
  getPromptPresets(): Observable<PromptPreset[]> {
    return this.http.get<PromptPreset[]>(`${this.baseUrl}/prompt-presets`)
      .pipe(catchError(this.handleError));
  }

  savePromptPresets(presets: PromptPreset[]): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/prompt-presets`, presets)
      .pipe(catchError(this.handleError));
  }

  // Cache management methods
  private getCachedData<T>(key: string, fetcher: () => Observable<T>): Observable<T> {
    if (this.cache.has(key)) {
      const cached = this.cache.get(key)!;
      if (cached.value !== undefined) {
        return of(cached.value);
      }
    } else {
      this.cache.set(key, new BehaviorSubject<T | undefined>(undefined));
    }

    // Fetch from server
    return fetcher().pipe(
      tap(data => {
        this.setCacheValue(key, data);
      })
    );
  }

  private getCacheValue<T>(key: string): T | undefined {
    const cached = this.cache.get(key);
    return cached ? cached.value : undefined;
  }

  private setCacheValue<T>(key: string, value: T): void {
    if (!this.cache.has(key)) {
      this.cache.set(key, new BehaviorSubject<T | undefined>(undefined));
    }
    this.cache.get(key)!.next(value);
  }

  private updateCacheValue<T>(key: string, updates: Partial<T>): void {
    const currentValue = this.getCacheValue<T>(key);
    if (currentValue) {
      const updatedValue = { ...currentValue, ...updates };
      this.setCacheValue(key, updatedValue);
    }
  }

  private clearCache(): void {
    this.cache.forEach(subject => subject.next(undefined));
  }

  // Observable streams for reactive components
  playlistSettings$ = this.getCachedObservable<PlaylistGenerationSettings>('playlist_settings');
  userPreferences$ = this.getCachedObservable<UserPreferences>('preferences');
  themePreferences$ = this.getCachedObservable<ThemePreferences>('theme');
  uiPreferences$ = this.getCachedObservable<UIViewPreferences>('ui_preferences');
  playHistory$ = this.getCachedObservable<any[]>('play_history');

  private getCachedObservable<T>(key: string): Observable<T | undefined> {
    if (!this.cache.has(key)) {
      this.cache.set(key, new BehaviorSubject<T | undefined>(undefined));
    }
    return this.cache.get(key)!.asObservable();
  }

  private handleError = (error: any): Observable<never> => {
    console.error('Backend storage error:', error);
    
    let errorMessage = 'An error occurred while accessing user data';
    
    if (error.status === 401) {
      errorMessage = 'Please log in to access your data';
    } else if (error.status === 500) {
      errorMessage = 'Server error. Please try again later';
    } else if (error.error?.error) {
      errorMessage = error.error.error;
    }

    return throwError(() => new Error(errorMessage));
  };
}